import React, { useMemo } from "react";
import { Box, Stack, useTheme } from "@mui/material";

/**
 * Pantalla de carga unificada de SmartDx (mockup "Logo con shimmer").
 * El wordmark SmartDx con un brillo que lo recorre + sublabel.
 * Theme-aware (dark/light) y respeta prefers-reduced-motion.
 *
 * Reemplaza los "Cargando..." sueltos del splash (Authentication) y los route guards.
 */
const LoadingScreen = ({ label = "Preparando tu espacio", fullscreen = true }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const bright = isDark ? "#ffffff" : "#0f172a";
  const accentSoft = "#508AA8";
  const baseRGBA = isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.20)";
  const accentBaseRGBA = "rgba(31,111,151,0.30)";

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      sx={{
        position: fullscreen ? "fixed" : "relative",
        inset: fullscreen ? 0 : "auto",
        width: fullscreen ? "100%" : "auto",
        minHeight: fullscreen ? "100%" : 220,
        zIndex: fullscreen ? 2000 : "auto",
        backgroundColor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack alignItems="center" spacing={2.5}>
        <Box
          aria-label="SmartDx"
          sx={{
            position: "relative",
            fontSize: { xs: 30, sm: 36 },
            fontWeight: 750,
            letterSpacing: "-0.04em",
            lineHeight: 1,
            color: baseRGBA,
            userSelect: "none",
            "& .dx": { color: accentBaseRGBA },
            "&::after": {
              content: '"SmartDx"',
              position: "absolute",
              inset: 0,
              backgroundImage: prefersReducedMotion
                ? "none"
                : `linear-gradient(100deg, transparent 20%, ${bright} 45%, ${accentSoft} 55%, transparent 80%)`,
              backgroundSize: "220% 100%",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: prefersReducedMotion ? bright : "transparent",
              animation: prefersReducedMotion ? "none" : "smartdxShimmer 1.8s linear infinite",
            },
            "@keyframes smartdxShimmer": {
              "0%": { backgroundPosition: "120% 0" },
              "100%": { backgroundPosition: "-120% 0" },
            },
          }}
        >
          Smart<span className="dx">Dx</span>
        </Box>
        {label ? (
          <Box
            sx={{
              color: "text.disabled",
              fontSize: 12,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
            }}
          >
            {label}
          </Box>
        ) : null}
      </Stack>
    </Box>
  );
};

export default LoadingScreen;
